import React, { useEffect, useState } from 'react';
import { NavLink, Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContexte';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAccess } from '../../contexts/AccessContext';
import LanguageSwitch from './LanguageSwitch';
import Icon from '../ui/Icon';
import Logo from '../ui/Logo';
import ServerIcon from '../ui/ServerIcon';
import './AdminSidebar.css';

const COLLAPSED_KEY = 'C2_SIDEBAR_COLLAPSED';

// Pages de l'espace admin, dans l'ordre du menu (réutilisé par le tableau de bord),
// limitées aux droits de l'utilisateur sur ce serveur (`can` vient de useAccess)
export const getAdminPages = (serverId, t, can) => [
    { path: `/server/${serverId}`, end: true, icon: 'home', label: t('navigation.dashboard') },
    { path: `/server/${serverId}/configuration`, permission: 'configuration', icon: 'sliders', label: t('navigation.configuration'), description: t('dashboard.serverConfig') },
    { path: `/server/${serverId}/bareme`, permission: 'bareme', icon: 'scale', label: t('navigation.bareme'), description: t('dashboard.scoreSettings') },
    { path: `/server/${serverId}/tempo`, permission: 'tempo', icon: 'clock', label: t('navigation.tempo'), description: t('dashboard.tempoManagement') },
    { path: `/server/${serverId}/saison`, permission: 'saisons', icon: 'trophy', label: t('navigation.seasons'), description: t('dashboard.seasonManagement') },
    { path: `/server/${serverId}/saisons/merge`, permission: 'fusion', icon: 'copy', label: t('navigation.merge', 'Fusion'), description: t('dashboard.mergeManagement', 'Fusion des classements entre serveurs') },
    // Événements : page masquée pour le moment (code conservé, voir ConfigEventPage)
    // { path: `/server/${serverId}/evenement`, permission: 'evenements', icon: 'calendar', label: t('navigation.events'), description: t('dashboard.eventManagement') },
    { path: `/server/${serverId}/zones`, permission: 'zones', icon: 'map', label: t('navigation.zones', 'Config des zones'), description: t('dashboard.zonesManagement', 'Zones proposées aux réservations') },
    { path: `/server/${serverId}/reservation`, permission: 'reservation', icon: 'clipboard', label: t('navigation.reservation'), description: t('dashboard.reservationManagement') },
    { path: `/server/${serverId}/encheres`, permission: 'encheres', icon: 'hammer', label: t('navigation.auctions', 'Enchères'), description: t('dashboard.auctionsManagement', 'Zones libres aux enchères') },
    { path: `/server/${serverId}/admin-stats`, permission: 'statistiques', icon: 'chart', label: t('navigation.statistics'), description: t('dashboard.statisticsManagement') },
    { path: `/server/${serverId}/pepites`, permission: 'pepites', icon: 'gem', label: t('navigation.pepites'), description: t('dashboard.pepitesManagement') },
    { path: `/server/${serverId}/rattrapage`, permission: 'rattrapage', icon: 'history', label: t('navigation.recovery', 'Rattrapage'), description: t('dashboard.recoveryManagement', 'Messages manqués par le bot, à valider') },
    { path: `/server/${serverId}/pl`, permission: 'pl', icon: 'backpack', label: t('navigation.pl', 'Service PL'), description: t('dashboard.plManagement', 'Bénéficiaires et prêts des panoplies sagesse') },
    { path: `/server/${serverId}/personnages`, permission: 'personnages', icon: 'users', label: t('navigation.personnages', 'Personnages'), description: t('dashboard.personnagesManagement', 'Personnages des membres et demandes à valider') },
].filter(page => !page.permission || can(serverId, page.permission));

const readCollapsed = () => {
    try {
        return localStorage.getItem(COLLAPSED_KEY) === '1';
    } catch {
        return false;
    }
};

/**
 * Menu latéral des espaces d'un serveur (administration par défaut, réutilisé par l'espace membre).
 * - pages : entrées du menu (par défaut les pages admin autorisées)
 * - badge : { label, tone: 'green' | 'purple' } affiché sous le nom du serveur
 * - switchLink : { to, label, icon } lien vers l'autre espace (admin ↔ membre)
 */
const AdminSidebar = ({ server, pages: customPages, badge, switchLink }) => {
    const { serverId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const { user, logout } = useAuth();
    const { can } = useAccess();
    const [collapsed, setCollapsed] = useState(readCollapsed);
    const [mobileOpen, setMobileOpen] = useState(false);

    // Le tiroir mobile se referme à chaque changement de page
    useEffect(() => { setMobileOpen(false); }, [location.pathname]);

    const toggleCollapsed = () => {
        setCollapsed(current => {
            try { localStorage.setItem(COLLAPSED_KEY, current ? '0' : '1'); } catch { /* stockage indisponible */ }
            return !current;
        });
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const pages = customPages || getAdminPages(serverId, t, can);
    const badgeInfo = badge || { label: t('adminHome.adminArea', 'Administration'), tone: 'green' };
    // Depuis l'admin : lien vers l'espace membre
    const otherSpace = switchLink !== undefined ? switchLink : { to: `/server/${serverId}/member`, label: t('adminHome.memberArea', 'Espace membre'), icon: 'user' };

    return (
        <>
            {/* Barre du haut, mobile uniquement */}
            <header className="c2-scope admin-mobilebar">
                <button type="button" className="c2-icon-btn" onClick={() => setMobileOpen(true)} aria-label={t('adminHome.openMenu', 'Ouvrir le menu')}>
                    <Icon name="menu" size={22} />
                </button>
                <span className="admin-mobilebar__title">{server?.name}</span>
                <ServerIcon guild={server} size={32} />
            </header>

            {mobileOpen && <div className="admin-sidebar__backdrop" onClick={() => setMobileOpen(false)} aria-hidden="true" />}

            <aside className={`c2-scope admin-sidebar${collapsed ? ' is-collapsed' : ''}${mobileOpen ? ' is-open' : ''}`}>
                <div className="admin-sidebar__top">
                    <Link to="/welcome" className="admin-sidebar__brand" title={t('adminHome.backToServers', 'Mes serveurs')}>
                        <Logo size={44} />
                        <span className="admin-sidebar__brand-name">LadderPVP</span>
                    </Link>
                    <button
                        type="button"
                        className="c2-icon-btn admin-sidebar__close"
                        onClick={() => setMobileOpen(false)}
                        aria-label={t('adminHome.closeMenu', 'Fermer le menu')}
                    >
                        <Icon name="close" size={20} />
                    </button>
                </div>

                <div className="admin-sidebar__server" title={server?.name}>
                    <ServerIcon guild={server} size={40} />
                    <div className="admin-sidebar__server-text">
                        <span className="admin-sidebar__server-name">{server?.name}</span>
                        <span className={`c2-badge c2-badge--${badgeInfo.tone}`}><span className={`c2-diamond${badgeInfo.tone === 'purple' ? ' c2-diamond--purple' : ''}`} />{badgeInfo.label}</span>
                    </div>
                </div>

                <nav className="admin-sidebar__nav" aria-label={badgeInfo.label}>
                    {pages.map(page => (
                        <NavLink
                            key={page.path}
                            to={page.path}
                            end={page.end}
                            className={({ isActive }) => `admin-nav-item${isActive ? ' is-active' : ''}`}
                            title={collapsed ? page.label : undefined}
                        >
                            <Icon name={page.icon} size={20} />
                            <span className="admin-nav-item__label">{page.label}</span>
                        </NavLink>
                    ))}
                </nav>

                <div className="admin-sidebar__bottom">
                    {otherSpace && (
                        <Link to={otherSpace.to} className="admin-nav-item" title={collapsed ? otherSpace.label : undefined}>
                            <Icon name={otherSpace.icon} size={20} />
                            <span className="admin-nav-item__label">{otherSpace.label}</span>
                        </Link>
                    )}
                    <Link to="/welcome" className="admin-nav-item" title={collapsed ? t('adminHome.backToServers', 'Mes serveurs') : undefined}>
                        <Icon name="arrowLeft" size={20} />
                        <span className="admin-nav-item__label">{t('adminHome.backToServers', 'Mes serveurs')}</span>
                    </Link>

                    <div className="admin-sidebar__account">
                        {user?.avatar ? (
                            <img className="admin-sidebar__avatar" src={`https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png`} alt="" />
                        ) : (
                            <span className="admin-sidebar__avatar admin-sidebar__avatar--empty"><Icon name="user" size={16} /></span>
                        )}
                        <span className="admin-sidebar__username">{user?.username}</span>
                        <div className="admin-sidebar__account-actions">
                            <LanguageSwitch variant="c2" />
                            <button type="button" className="c2-icon-btn" onClick={handleLogout} aria-label={t('common.logout')} title={t('common.logout')}>
                                <Icon name="logout" size={18} />
                            </button>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="admin-sidebar__collapse"
                        onClick={toggleCollapsed}
                        aria-label={collapsed ? t('adminHome.expand', 'Agrandir le menu') : t('adminHome.collapse', 'Réduire le menu')}
                        title={collapsed ? t('adminHome.expand', 'Agrandir le menu') : t('adminHome.collapse', 'Réduire le menu')}
                    >
                        <Icon name={collapsed ? 'chevronsRight' : 'chevronsLeft'} size={18} />
                        <span className="admin-nav-item__label">{t('adminHome.collapse', 'Réduire le menu')}</span>
                    </button>
                </div>
            </aside>
        </>
    );
};

export default AdminSidebar;
